import type { FunctionComponent } from 'preact';

import { isolatedHeader } from 'Addons/fitmentSearch/isolatedKeys';
import FacetToggle, {
  type Params as FacetToggleParams,
  type Props as FacetToggleProps,
} from 'Components/smallComponents/facetToggle';
import { createSelectRepeater, SelectContextProvider } from 'Components/smallComponents/select';
import type { TemplateFunction } from 'Components/types';
import {
  discardVehicleFieldsRequested,
  isolatedDiscardVehicle,
  replaceVehicleValue,
  vehicleSelected,
} from 'Core/actions/fitmentSearch/index';
import {
  createAllRequiredFieldsHaveSelectionSelector,
  createFilteredFitmentSearchResponseFieldsSelector,
  createFitmentSearchResponseSelectionSelector,
  createRequiredFitmentSearchFieldsSelector,
  fitmentSearchBaseFieldsSelector,
  fitmentSearchExtraFieldsSelector,
  fitmentSearchFieldsSelector,
  garageSizeSelector,
  isSpecifyDialogOpenedSelector,
} from 'Core/selectors/fitmentSearch/index';
import { useDispatch, useSelector } from 'Core/store';
import { Vehicle } from 'Models/index';
import fitmentSearchConfig from 'Models/uiConfig/fitmentSearchConfig';
import { cloneSafe } from 'Utils/components';
import isTruthy from 'Utils/isTruthy';
import safeTemplateCall from 'Utils/safeTemplateCall';

import type { InnerProps as Props } from './vehicleDropdowns';

const ActualDropdowns: FunctionComponent<Props> = ({
  template,
  name: widgetName,
  aboutToChange,
  appendedClasses,
  collapsing,
  facets,
  fields,
  isAutoVehicleSelectionDisabled: isAutoVehicleSelectionDisabledProp,
  isolatedKey,
  isVehicleSelected,
  facetToggleConfig,
  onSelectVehicle,
  onChangeVehicle,
  openDialog,
  rootRef,
  useNativeDropdown,
  globalVehicleDiscardEnabled,
  redirectUrl,
  doNotRedirectOnVehicleSelect,
  isVehicleSelectionForced,
  useSearchableDropdown,
  autoOpenDropdowns,
  isVehicleSelectionIsolated,
  autoSyncVisualization,
}) => {
  const dispatch = useDispatch();

  const responseFields = useSelector(createFilteredFitmentSearchResponseFieldsSelector(isolatedKey));
  const requiredFields = useSelector(createRequiredFitmentSearchFieldsSelector(isolatedKey));
  const selection = useSelector(createFitmentSearchResponseSelectionSelector(isolatedKey));
  const isAllRequiredFieldHasSelection = useSelector(
    createAllRequiredFieldsHaveSelectionSelector(isolatedKey),
  );
  const unselectedFacets = facets.filter((facet) => !facet.selection.length);
  const fitmentFields = useSelector(fitmentSearchFieldsSelector);
  const baseFitmentFields = useSelector(fitmentSearchBaseFieldsSelector);
  const extraFitmentFields = useSelector(fitmentSearchExtraFieldsSelector);
  const isSpecifyDialogOpened = useSelector(isSpecifyDialogOpenedSelector);
  const vehicleListSize = useSelector(garageSizeSelector);

  // if a user select a vehicle without some extra fields manually by the button,
  // mark those fields as ignored not to show the specify dialog for them
  const shouldNotIgnoreFields = fitmentSearchConfig.forceSpecifyDialog && isolatedKey === isolatedHeader;
  const vehicle = new Vehicle(
    responseFields
      .map(
        (field) =>
          selection.find((v) => v.field === field) ||
          (!requiredFields?.includes?.(field) &&
            !shouldNotIgnoreFields && { field, term: '__ignored', value: '__ignored' }),
      )
      .filter(isTruthy),
    responseFields,
  );

  const selectVehicle = () => {
    if (isAllRequiredFieldHasSelection) {
      dispatch(
        vehicleSelected(vehicle, {
          doNotRedirectOnVehicleSelect,
          isSpecifyDialogOpened,
          isVehicleSelectionForced,
          isolatedKey,
          redirectUrl,
          sendIsolatedRequest: isolatedKey !== null,
          vehicleWidgetName: widgetName,
        }),
      );

      fitmentSearchConfig.onVehicleSelected(vehicle.filteredFieldMap);
      onSelectVehicle();

      if (!collapsing.collapsed) {
        collapsing.toggleCollapsed();
      }
    }
  };
  const discardVehicle = () => {
    dispatch(isolatedDiscardVehicle(isolatedKey, globalVehicleDiscardEnabled));
    if (isAllRequiredFieldHasSelection) {
      fitmentSearchConfig.onVehicleDiscarded();
    }
  };
  const discardExtraFields = () =>
    dispatch(discardVehicleFieldsRequested(isolatedKey, fitmentFields, extraFitmentFields));
  const discardFields = (fields: string[]) =>
    dispatch(discardVehicleFieldsRequested(isolatedKey, fitmentFields, fields));

  const isVehicleSpecs = widgetName === 'VehicleSpecs';

  const getInitialOpenIndex = () => {
    const firstUnselectedIndex = facets.findIndex(
      ({ facetedValues }) => !facetedValues.some((v) => v.isSelected),
    );

    // If all fields have selections, don't auto-open any
    if (firstUnselectedIndex === -1) {
      return -1;
    }

    // Handle first field: 'all' opens it, 'next' doesn't
    if (firstUnselectedIndex === 0) {
      return autoOpenDropdowns === 'all' ? 0 : -1;
    }

    // For subsequent fields, open only if all previous fields are selected
    if (autoOpenDropdowns === 'all' || autoOpenDropdowns === 'next') {
      const allPreviousSelected = facets
        .slice(0, firstUnselectedIndex)
        .every(({ facetedValues }) => facetedValues.some((v) => v.isSelected));

      return allPreviousSelected ? firstUnselectedIndex : -1;
    }

    return -1;
  };

  const initialOpenIndex = getInitialOpenIndex();

  const selects = createSelectRepeater(
    facets.map(({ field, name, facetedValues }, index) => ({
      entries: facetedValues.map((v) => ({
        value: v.value,
        term: v.term,
        payload: v.payload,
        selected: v.isSelected,
      })),
      extraClassName: 'cm_vehicle-widget_select',
      disabled: !facetedValues.length,
      hideNullOption: true,
      field,
      title: name,
      key: field,
      index,
      useNativeDropdown: !useSearchableDropdown && useNativeDropdown,
      isSearchable: useSearchableDropdown,
      initialOpen: autoOpenDropdowns && index === initialOpenIndex && facetedValues.length > 0,
      onChange: (term) => {
        const facetValue = facetedValues.find((v) => v.term === term);
        if (facetValue) {
          const isAutoVehicleSelectionDisabled =
            !isVehicleSpecs &&
            (isAutoVehicleSelectionDisabledProp ?? fitmentSearchConfig.isAutoVehicleSelectionDisabled);

          dispatch(
            replaceVehicleValue(facetValue, isolatedKey, fitmentFields, {
              isAutoVehicleSelectionDisabled,
              isPartialMode: !!fields,
              sendIsolatedRequest: isolatedKey !== null || isAutoVehicleSelectionDisabled,
              doNotRedirectOnVehicleSelect,
              redirectUrl,
              vehicleWidgetName: widgetName,
              isVehicleSelectionIsolated,
            }),
          );
        }
      },
    })),
  );

  const facetToggle = (templ: TemplateFunction<FacetToggleParams>) => {
    if (!facetToggleConfig) {
      return null;
    }

    const props: FacetToggleProps = {
      ...facetToggleConfig,
      template: templ,
    };

    return <FacetToggle {...props} key="facet-toggle" />;
  };

  const component = safeTemplateCall(
    template,
    {
      name: widgetName,
      selects,
      vehicle: vehicle.filteredFieldMap,
      vehicleString: vehicle.toString(),
      vehicleBaseValues: vehicle.getFilteredValues(baseFitmentFields),
      vehicleExtraValues: vehicle.getFilteredValues(extraFitmentFields),
      vehicleListSize,
      allowToSetVehicle: isAllRequiredFieldHasSelection,
      allowToDiscardVehicle: selection.length > 0,
      aboutToChange,
      discardVehicle,
      discardExtraFields,
      discardFields,
      selectVehicle,
      openDialog,
      facetToggle,
      changeVehicle: onChangeVehicle,
      fitmentSearchTitle: fitmentSearchConfig.fitmentSearchTitle,
      template: 'active',
      isVehicleSelected,
      selection: Object.fromEntries(selection.map((select) => [select.field, select.value])),
      isVehicleSelectionForced,
      isAllFieldsSelected: !unselectedFacets.length,
      autoSyncVisualization,
      ...collapsing,
    },
    'ActualDropdowns',
  );

  return <SelectContextProvider>{cloneSafe(component, rootRef, { appendedClasses })}</SelectContextProvider>;
};

export default ActualDropdowns;
